import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { classNames } from "../../helpers/classNames";

export default function Tags({
  label,
  labelClasses,
  required,
  errorType,
  errorText,
  onChange = () => {},
  dropdownData = [],
  value = [],
  placeholder = "Enter tags",
  filteredBy = "value",
}) {
  const divRef = useRef(null);
  const spanRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(value);
  const [options, setOptions] = useState(dropdownData);

  useEffect(() => {
    if (spanRef.current && inputRef.current) {
      const width = spanRef.current.offsetWidth;
      inputRef.current.style.width = `${width}px`;
    }
  }, [query]);

  useEffect(() => {
    setOptions(dropdownData || []);
  }, [dropdownData?.length]);

  useEffect(() => {
    setSelected(value || []);
  }, [value?.length]);

  const filteredOptions = useMemo(() => {
    return options.filter(
      (person) =>
        person &&
        typeof person.label === "string" &&
        person.label.toLowerCase().includes(query.toLowerCase())
    );
  }, [options?.length]);

  const addTagFromInput = () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    const alreadyExists =
      selected?.some(
        (item) =>
          item &&
          typeof item.label === "string" &&
          item.label.toLowerCase() === trimmed.toLowerCase()
      ) ||
      options?.some(
        (item) =>
          item &&
          typeof item.label === "string" &&
          item.label.toLowerCase() === trimmed.toLowerCase()
      );

    if (!alreadyExists) {
      const newTag = { value: Date.now(), label: trimmed };
      setOptions((prev) => [...prev, newTag]);
      setSelected((prev) => [...prev, newTag]);
      onChange([...selected, newTag]?.filter(Boolean));
    }

    setQuery("");
  };

  return (
    <div ref={divRef} className="relative w-full">
      {label && (
        <div
          className={
            "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck " +
            labelClasses
          }
        >
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <Combobox
        as={"div"}
        multiple
        value={selected}
        onChange={(val) => {
          setSelected(val);
          onChange(Array.isArray(val) ? val?.filter(Boolean) : val);
        }}
        by={filteredBy}
      >
        {(val) => (
          <>
            <div
              className={classNames(
                "group w-full flex items-center rounded overflow-hidden relative",
                "rounded-md placeholder:text-text-disabled bg-surface-04 border border-text-secondary/50 px-3 font-CanelaDeck text-sm text-deep-purple !ring-0 !outline-0 focus:border-text-disabled transition-all duration-200"
              )}
              onClick={() => {
                inputRef.current?.focus();
              }}
            >
              <div className="flex flex-grow w-full whitespace-nowrap overflow-hidden">
                <ul
                  className={classNames(
                    "flex gap-2 py-1 h-full overflow-y-auto min-h-12 max-h-auto w-full",
                    selected?.length > 0 ? "flex-wrap" : "items-center"
                  )}
                >
                  {selected?.map((item) =>
                    item && typeof item.label === "string" ? (
                      <li
                        key={item.value}
                        className="text-sm flex items-center gap-2 px-2 h-8 py-[2px] rounded bg-primary/30"
                      >
                        <span>{item.label}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const filtered = selected?.filter(
                              (elm) => elm?.value !== item?.value
                            );
                            onChange(filtered?.filter(Boolean));
                            setSelected(filtered);
                          }}
                        >
                          <i className="fa-fw fa-regular fa-xmark"></i>
                        </button>
                      </li>
                    ) : null
                  )}
                  <li className="max-w-full p-1 overflow-hidden relative">
                    <span
                      ref={spanRef}
                      className="absolute invisible whitespace-pre px-0 py-0 text-sm"
                      style={{
                        fontFamily: "inherit",
                        fontSize: "inherit",
                        whiteSpace: "pre",
                      }}
                    >
                      {query || " "}
                    </span>
                    <ComboboxInput
                      ref={inputRef}
                      className="outline-none ring-0 p-0 text-sm bg-transparent focus:outline-none border-0 focus:border-0 focus:right-0 focus:ring-transparent"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e?.code === "Space") {
                          const newValue = e.target.value + " ";
                          setQuery(newValue);
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTagFromInput();
                        }
                      }}
                    />
                  </li>
                  {!query && selected?.length === 0 && (
                    <span className="text-gray-500 -ml-4">{placeholder}</span>
                  )}
                </ul>
              </div>
              <ComboboxButton className="my-auto py-1 text-gray-500">
                <i className="fa-solid fa-angle-down text-sm"></i>
              </ComboboxButton>
            </div>

            {(filteredOptions.length > 0 || query.trim() !== "") && (
              <ComboboxOptions
                anchor="bottom"
                transition
                className=" mt-1 max-h-60 w-56 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 relative"
              >
                {filteredOptions.map((person) =>
                  person && typeof person.label === "string" ? (
                    <ComboboxOption
                      key={person.value}
                      value={person}
                      className={({ active, selected }) =>
                        classNames(
                          "cursor-default select-none px-4 py-2 flex gap-2 items-center",
                          active ? "bg-primary/30" : "",
                          selected ? "font-semibold" : "font-normal"
                        )
                      }
                    >
                      <span className="flex items-center justify-center h-5 w-5">
                        {selected && (
                          <i className="fa-fw fa-regular fa-check"></i>
                        )}
                      </span>
                      <span>{person.label}</span>
                    </ComboboxOption>
                  ) : null
                )}
                {query.trim() !== "" &&
                  !filteredOptions.some(
                    (p) =>
                      p &&
                      typeof p.label === "string" &&
                      p.label.toLowerCase() === query.trim().toLowerCase()
                  ) && (
                    <ComboboxOption
                      as="div"
                      className="cursor-pointer px-4 py-2 text-primary hover:bg-primary/10"
                      onClick={addTagFromInput}
                    >
                      Add “{query}”
                    </ComboboxOption>
                  )}
              </ComboboxOptions>
            )}
          </>
        )}
      </Combobox>
      {errorType === "error" && (
        <div className="text-xs text-red-600 mt-1">
          <i className="fa-regular fa-fw fa-square-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType === "warning" && (
        <div className="text-xs text-amber-600 mt-1">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType === "success" && (
        <div className="text-xs text-primary mt-1">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType === "info" && (
        <div className="text-xs text-sky-600 mt-1">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
}
